import { useMemo, useState } from "react";
import { slugify } from "../lib/slugify";
import { WORKSPACE_TEMPLATES } from "../data/workspaceTemplates";

const ONBOARDING_STEPS = [
  { key: "type", label: "Workspace" },
  { key: "business", label: "Business" },
  { key: "public", label: "Public page" },
  { key: "modules", label: "Modules" },
  { key: "launch", label: "Launch" }
];

const STARTING_MODULES = [
  {
    key: "membership",
    label: "Membership",
    detail: "Applications, member records, plans, waivers, and owner review.",
    applicationKey: "membership",
    templates: ["physical_range", "chapter_organization", "training_business"]
  },
  {
    key: "resources",
    label: "Range facilities",
    detail: "Physical zones, ranges, lanes, and member-visible facility setup.",
    applicationKey: "resources",
    templates: ["physical_range"]
  },
  {
    key: "reservations",
    label: "Reservations",
    detail: "Booking calendar, availability checks, and member booking rules.",
    applicationKey: "reservations",
    templates: ["physical_range"]
  },
  {
    key: "events",
    label: "Programs and events",
    detail: "Classes, rosters, attendance, approvals, and event payments.",
    applicationKey: "events",
    templates: ["physical_range", "chapter_organization", "training_business"]
  },
  {
    key: "payments",
    label: "Payments",
    detail: "Stripe setup, checkout readiness, receipts, and reconciliation.",
    applicationKey: "payments",
    templates: ["physical_range", "chapter_organization", "training_business"]
  },
  {
    key: "insights",
    label: "Insights",
    detail: "Member growth, range activity, retention signals, and owner journey views.",
    applicationKey: "insights",
    templates: ["physical_range"]
  },
  {
    key: "chapters",
    label: "Chapters",
    detail: "Chapter directory, chapter leaders, members, and local programming.",
    applicationKey: "chapters",
    templates: ["chapter_organization"]
  },
  {
    key: "staff-portal",
    label: "Staff and chapter workspaces",
    detail: "Scoped portals for chapter leads, instructors, and operators.",
    applicationKey: "staff-portal",
    templates: ["physical_range", "chapter_organization", "training_business"]
  },
];

const TEMPLATE_LABELS = {
  physical_range: "Range facility",
  chapter_organization: "Chapter organization",
  training_business: "Training business"
};

function templateLabel(template) {
  return TEMPLATE_LABELS[template.templateKey] || template.label;
}

function availableModules(templateKey) {
  return STARTING_MODULES.filter((module) => module.templates.includes(templateKey));
}

function moduleDefaultSelected(templateKey, moduleKey) {
  const defaults = {
    physical_range: ["membership", "resources", "reservations", "events", "payments", "insights"],
    chapter_organization: ["membership", "chapters", "events", "payments", "staff-portal"],
    training_business: ["membership", "events", "payments", "staff-portal"]
  };
  return (defaults[templateKey] || defaults.physical_range).includes(moduleKey);
}

function summarizeAddress(address = {}) {
  return [address.line1, address.city, address.region, address.postalCode]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
}

export function CreateRangePage({
  form,
  handleSubmit,
  saving,
  setForm,
  suggestedSlug
}) {
  const [stepIndex, setStepIndex] = useState(0);
  const currentStep = ONBOARDING_STEPS[stepIndex];
  const selectedTemplate = WORKSPACE_TEMPLATES.find((template) => template.templateKey === form.templateKey) || WORKSPACE_TEMPLATES[0];
  const modules = useMemo(() => availableModules(form.templateKey), [form.templateKey]);
  const selectedApplicationKeys = form.selectedApplicationKeys || [];
  const publicSlug = form.slug || suggestedSlug || "";
  const canContinue = stepIndex === 0 ||
    (stepIndex === 1 && form.name.trim() && form.contactEmail.trim()) ||
    (stepIndex === 2 && publicSlug.trim()) ||
    (stepIndex === 3 && selectedApplicationKeys.length > 0) ||
    stepIndex === 4;

  function patchForm(patch) {
    setForm((current) => ({ ...current, ...patch }));
  }

  function patchAddress(patch) {
    setForm((current) => ({
      ...current,
      address: {
        ...current.address,
        ...patch
      }
    }));
  }

  function selectTemplate(templateKey) {
    const nextModules = availableModules(templateKey)
      .filter((module) => moduleDefaultSelected(templateKey, module.key))
      .map((module) => module.applicationKey);
    setForm((current) => ({
      ...current,
      templateKey,
      selectedApplicationKeys: nextModules
    }));
  }

  function toggleModule(applicationKey) {
    setForm((current) => {
      const currentKeys = current.selectedApplicationKeys || [];
      const nextKeys = currentKeys.includes(applicationKey)
        ? currentKeys.filter((key) => key !== applicationKey)
        : [...currentKeys, applicationKey];
      return { ...current, selectedApplicationKeys: nextKeys };
    });
  }

  function goNext() {
    if (!canContinue) {
      return;
    }
    setStepIndex((current) => Math.min(current + 1, ONBOARDING_STEPS.length - 1));
  }

  function goBack() {
    setStepIndex((current) => Math.max(current - 1, 0));
  }

  function submitOnboarding(event) {
    handleSubmit(event);
  }

  return (
    <section className="owner-onboarding-page">
      <div className="owner-onboarding-hero">
        <div>
          <p className="eyebrow">Owner onboarding</p>
          <h2>Start your RangeGrid workspace</h2>
          <p>Create the owner workspace first. Billing stays in trial mode while you finish launch setup.</p>
        </div>
        <div className="owner-onboarding-trial">
          <span>Trial workspace</span>
          <strong>No production billing required today</strong>
        </div>
      </div>

      <div className="owner-onboarding-shell">
        <aside className="owner-onboarding-steps" aria-label="Onboarding steps">
          {ONBOARDING_STEPS.map((step, index) => (
            <button
              type="button"
              className={index === stepIndex ? "is-active" : index < stepIndex ? "is-complete" : ""}
              key={step.key}
              onClick={() => setStepIndex(index)}
            >
              <span>{index + 1}</span>
              <strong>{step.label}</strong>
            </button>
          ))}
        </aside>

        <form className="owner-onboarding-panel" onSubmit={submitOnboarding}>
          {currentStep.key === "type" && (
            <div className="owner-onboarding-section">
              <div className="panel-header">
                <h3>Choose your workspace type</h3>
                <p>This sets the starting language and applications. You can adjust everything later.</p>
              </div>
              <div className="workspace-template-options">
                {WORKSPACE_TEMPLATES.map((template) => {
                  const selected = form.templateKey === template.templateKey;
                  return (
                    <label className={`workspace-template-card ${selected ? "is-selected" : ""}`} key={template.templateKey}>
                      <input
                        type="radio"
                        name="templateKey"
                        value={template.templateKey}
                        checked={selected}
                        onChange={(event) => selectTemplate(event.target.value)}
                      />
                      <span>{template.eyebrow}</span>
                      <strong>{templateLabel(template)}</strong>
                      <small>{template.description}</small>
                      <em>{template.seededApps}</em>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {currentStep.key === "business" && (
            <div className="owner-onboarding-section">
              <div className="panel-header">
                <h3>Business basics</h3>
                <p>Use the name and contact details members should recognize.</p>
              </div>
              <div className="settings-grid">
                <label>
                  Public workspace name
                  <input
                    value={form.name}
                    onChange={(event) => patchForm({ name: event.target.value, slug: form.slug ? form.slug : slugify(event.target.value) })}
                    placeholder="Liberty Long Range"
                    required
                  />
                </label>
                <label>
                  Legal organization name
                  <input
                    value={form.legalName}
                    onChange={(event) => patchForm({ legalName: event.target.value })}
                    placeholder="Liberty Long Range LLC"
                  />
                </label>
                <label>
                  Contact email
                  <input
                    type="email"
                    value={form.contactEmail}
                    onChange={(event) => patchForm({ contactEmail: event.target.value })}
                    placeholder="owner@example.com"
                    required
                  />
                </label>
                <label>
                  Contact phone
                  <input
                    value={form.contactPhone}
                    onChange={(event) => patchForm({ contactPhone: event.target.value })}
                    placeholder="555-0100"
                  />
                </label>
                <label>
                  Timezone
                  <select value={form.timezone} onChange={(event) => patchForm({ timezone: event.target.value })}>
                    <option value="America/Los_Angeles">America/Los_Angeles</option>
                    <option value="America/Denver">America/Denver</option>
                    <option value="America/Chicago">America/Chicago</option>
                    <option value="America/New_York">America/New_York</option>
                  </select>
                </label>
                <label>
                  Address line 1
                  <input
                    value={form.address.line1}
                    onChange={(event) => patchAddress({ line1: event.target.value })}
                    placeholder="123 Range Road"
                  />
                </label>
                <label>
                  City
                  <input
                    value={form.address.city}
                    onChange={(event) => patchAddress({ city: event.target.value })}
                    placeholder="Liberty"
                  />
                </label>
                <label>
                  State or region
                  <input
                    value={form.address.region}
                    onChange={(event) => patchAddress({ region: event.target.value })}
                    placeholder="TX"
                  />
                </label>
                <label>
                  Postal code
                  <input
                    value={form.address.postalCode}
                    onChange={(event) => patchAddress({ postalCode: event.target.value })}
                    placeholder="77575"
                  />
                </label>
                <label>
                  Country
                  <input
                    value={form.address.country}
                    onChange={(event) => patchAddress({ country: event.target.value.toUpperCase().slice(0, 2) })}
                    placeholder="US"
                  />
                </label>
              </div>
            </div>
          )}

          {currentStep.key === "public" && (
            <div className="owner-onboarding-section">
              <div className="panel-header">
                <h3>Public identity</h3>
                <p>This creates the first public URL. Custom domains can be connected after launch setup.</p>
              </div>
              <div className="settings-grid">
                <label>
                  Public URL slug
                  <input
                    value={form.slug}
                    onChange={(event) => patchForm({ slug: slugify(event.target.value) })}
                    placeholder={suggestedSlug || "liberty-long-range"}
                  />
                </label>
                <label>
                  Existing website
                  <input
                    value={form.website}
                    onChange={(event) => patchForm({ website: event.target.value })}
                    placeholder="https://example.com"
                  />
                </label>
                <label className="full-span">
                  Public description
                  <textarea
                    value={form.description}
                    onChange={(event) => patchForm({ description: event.target.value })}
                    placeholder="A short public description for members and visitors."
                  />
                </label>
              </div>
            </div>
          )}

          {currentStep.key === "modules" && (
            <div className="owner-onboarding-section">
              <div className="panel-header">
                <h3>Starting modules</h3>
                <p>Choose the work areas you want ready in the owner console on day one.</p>
              </div>
              <div className="owner-module-grid">
                {modules.map((module) => {
                  const selected = selectedApplicationKeys.includes(module.applicationKey);
                  return (
                    <button
                      type="button"
                      className={selected ? "is-selected" : ""}
                      key={module.key}
                      onClick={() => toggleModule(module.applicationKey)}
                    >
                      <strong>{module.label}</strong>
                      <span>{module.detail}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {currentStep.key === "launch" && (
            <div className="owner-onboarding-section">
              <div className="panel-header">
                <h3>Review and create</h3>
                <p>Your workspace starts in trial mode. The launch checklist will guide the rest.</p>
              </div>
              <div className="owner-onboarding-summary">
                <div>
                  <span>Workspace</span>
                  <strong>{form.name || "Name needed"}</strong>
                  <small>{templateLabel(selectedTemplate)}</small>
                </div>
                <div>
                  <span>Public URL</span>
                  <strong>/r/{publicSlug || "workspace-slug"}</strong>
                  <small>{form.website || "Custom domain can be connected later"}</small>
                </div>
                <div>
                  <span>Contact</span>
                  <strong>{form.contactEmail || "Email needed"}</strong>
                  <small>{summarizeAddress(form.address) || "Address can be finished later"}</small>
                </div>
                <div>
                  <span>Modules</span>
                  <strong>{selectedApplicationKeys.length}</strong>
                  <small>{modules.filter((module) => selectedApplicationKeys.includes(module.applicationKey)).map((module) => module.label).join(", ")}</small>
                </div>
              </div>
              <div className="owner-launch-preview">
                <strong>After creation</strong>
                <span>Open the launch checklist for profile, operating hours, resources, membership setup, Stripe, staff access, website, and platform billing.</span>
              </div>
            </div>
          )}

          <div className="owner-onboarding-actions">
            <button
              type="button"
              className="secondary-button"
              disabled={stepIndex === 0 || saving}
              onClick={(event) => {
                event.preventDefault();
                goBack();
              }}
            >
              Back
            </button>
            {stepIndex < ONBOARDING_STEPS.length - 1 ? (
              <button
                type="button"
                disabled={!canContinue}
                onClick={(event) => {
                  event.preventDefault();
                  goNext();
                }}
              >
                Continue
              </button>
            ) : (
              <button type="submit" disabled={saving || !form.name.trim() || !form.contactEmail.trim() || selectedApplicationKeys.length === 0}>
                {saving ? "Creating workspace..." : "Create workspace"}
              </button>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}
